import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Icon from '../../components/assets/icons';
import Button from '../../components/common/Button';
import Flyout from '../../components/elements/Flyout';
import Modal from '../../components/elements/Modal';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useModal } from '../../hooks/useModal';
import { useAppSelector } from '../../hooks/useSelector';
import { getClassicDuel, getQuickDuel, getTeamDuel } from '../../store/selectors/settings.selector';
import { getInGame, getTeam } from '../../store/selectors/team.selector';
import { settingsActions } from '../../store/slices/settings.slice';
import { teamActions } from '../../store/slices/team.slice';

import PauseContainer from './containers/pause-container';
import TimerContainer from './containers/timer-container';
import StartGameForm from './forms/StartGame';
import { useSettings } from './hooks/useSettings';
import * as S from './styles';

function MainPage() {
  const dispatch = useAppDispatch();
  const team = useAppSelector(getTeam);
  const classicSettings = useAppSelector(getClassicDuel);
  const teamSettings = useAppSelector(getTeamDuel);
  const quickSettings = useAppSelector(getQuickDuel);
  const inGame = useAppSelector(getInGame);
  const [params, setParams] = useSearchParams();
  const [activeGameMode, setActiveGameMode] = useState(params.get('game_mode') ?? 'classic game');

  const { isActive: isActivePauseModal, setActive: setPauseModalActive } = useModal({});
  const { isActive: isActiveVotersModal, setActive: setVotersModalActive } = useModal({});

  const {
    isActive: isActiveModal,
    toggleHandler: modalToggleHandler,
    setActive: setActiveModal,
  } = useModal({});

  const {
    setGameSettings,
    activeFlyout,
    isActive: isActiveFlyout,
    toggleHandler: flyoutToggleHandler,
  } = useSettings();

  const toggleFullScreen = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen();
    } else {
      if (document.exitFullscreen) {
        void document.exitFullscreen();
      }
    }
  }, []);

  const deactivateModal = useCallback(() => {
    setActiveModal(false);
  }, [setActiveModal]);

  const passTurn = useCallback(() => {
    const player = team.activePlayer === 'player1' ? 'player2' : 'player1';
    dispatch(teamActions.setPlaying({ player }));
    dispatch(teamActions.setActivePlayer({ player }));
  }, [team, dispatch]);

  const onClickPause = useCallback(
    (setActive: (value: boolean) => void, player?: 'player1' | 'player2') => () => {
      setActive(true);
      dispatch(teamActions.startPause({ player }));
    },
    [dispatch],
  );

  const onClickStop = useCallback(
    (player: 'player1' | 'player2') => () => {
      dispatch(teamActions.startPause({ player }));
      dispatch(teamActions.completeGame());
      if (player === 'player1') {
        dispatch(teamActions.updateTimerPlayer1());
      } else {
        dispatch(teamActions.updateTimerPlayer2());
      }
      if (params.get('game_mode') === 'classic game') {
        dispatch(
          settingsActions.setPartialDuel({
            player: player,
            settings: {
              min: classicSettings.classicDuel.roundTime.min,
              sec: classicSettings.classicDuel.roundTime.sec,
            },
          }),
        );
      } else if (params.get('game_mode') === 'team game') {
        dispatch(
          settingsActions.setPartialDuel({
            player: player,
            settings: {
              min: teamSettings.teamDuel.roundTime.min,
              sec: teamSettings.teamDuel.roundTime.sec,
            },
          }),
        );
      } else {
        dispatch(
          settingsActions.setPartialDuel({
            player: player,
            settings: {
              min: quickSettings.roundTime.min,
              sec: quickSettings.roundTime.sec,
            },
          }),
        );
      }
    },
    [dispatch, params],
  );

  const onChangeGameMode = useCallback(
    (title: string) => {
      params.set('game_mode', title);
      setActiveGameMode(title);
      setParams(params);
    },
    [params, setParams],
  );

  useEffect(() => {
    if (!params.get('game_mode')) {
      params.set('game_mode', 'classic game');
      setParams(params);
    }
  }, [params, setParams]);

  useEffect(() => {
    if (
      params.get('game_mode') === 'classic game' &&
      !team.player1.remainingTime &&
      !team.player2.remainingTime
    ) {
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: {
            min: classicSettings.classicDuel.roundTime.min,
            sec: classicSettings.classicDuel.roundTime.sec,
          },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: {
            min: classicSettings.classicDuel.roundTime.min,
            sec: classicSettings.classicDuel.roundTime.sec,
          },
        }),
      );
      dispatch(teamActions.completeGame());
      dispatch(teamActions.updateTimerKeys());
    } else if (
      params.get('game_mode') === 'team game' &&
      !team.player1.remainingTime &&
      !team.player2.remainingTime
    ) {
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: {
            min: teamSettings.teamDuel.roundTime.min,
            sec: teamSettings.teamDuel.roundTime.sec,
          },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: {
            min: teamSettings.teamDuel.roundTime.min,
            sec: teamSettings.teamDuel.roundTime.sec,
          },
        }),
      );
      dispatch(teamActions.completeGame());
      dispatch(teamActions.updateTimerKeys());
    } else if (!team.player1.remainingTime && !team.player2.remainingTime) {
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: {
            min: quickSettings.roundTime.min,
            sec: quickSettings.roundTime.sec,
          },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: {
            min: quickSettings.roundTime.min,
            sec: quickSettings.roundTime.sec,
          },
        }),
      );
      dispatch(teamActions.completeGame());
      dispatch(teamActions.updateTimerKeys());
    }
  }, [
    classicSettings.classicDuel.roundTime.min,
    classicSettings.classicDuel.roundTime.sec,
    teamSettings.teamDuel.roundTime.min,
    teamSettings.teamDuel.roundTime.sec,
    dispatch,
    params,
    quickSettings.roundTime.min,
    quickSettings.roundTime.sec,
    setParams,
    team.player1.remainingTime,
    team.player2.remainingTime,
  ]);

  useEffect(() => {
    setActiveGameMode(params.get('game_mode') ?? 'quick game');
    if (params.get('game_mode') === 'quick game') {
      dispatch(teamActions.setColors({ player1Color: 'red', player2Color: 'blue' }));
      dispatch(teamActions.setActivePlayer({ player: 'player1' }));
      dispatch(teamActions.setPlaying({ player: 'player1' }));
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: {
            min: quickSettings.roundTime.min,
            sec: quickSettings.roundTime.sec,
          },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: {
            min: quickSettings.roundTime.min,
            sec: quickSettings.roundTime.sec,
          },
        }),
      );
    } else if (params.get('game_mode') === 'team game') {
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: {
            min: teamSettings.teamDuel.roundTime.min,
            sec: teamSettings.teamDuel.roundTime.sec,
          },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: {
            min: teamSettings.teamDuel.roundTime.min,
            sec: teamSettings.teamDuel.roundTime.sec,
          },
        }),
      );
    } else {
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: {
            min: classicSettings.classicDuel.roundTime.min,
            sec: classicSettings.classicDuel.roundTime.sec,
          },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: {
            min: classicSettings.classicDuel.roundTime.min,
            sec: classicSettings.classicDuel.roundTime.sec,
          },
        }),
      );
    }
    dispatch(teamActions.resetActivePlayer());
    dispatch(teamActions.updateTimerKeys());
  }, [
    classicSettings.classicDuel.roundTime.min,
    classicSettings.classicDuel.roundTime.sec,
    teamSettings.teamDuel.roundTime.min,
    teamSettings.teamDuel.roundTime.sec,
    dispatch,
    params.get('game_mode'),
    quickSettings.roundTime.min,
    quickSettings.roundTime.sec,
  ]);

  // useEffect(() => {
  //   dispatch(
  //     teamActions.setNames({
  //       player1Name: team.player1.name || 'Player 1',
  //       player2Name: team.player2.name || 'Player 2',
  //     }),
  //   );
  // }, [dispatch, team.player1.name, team.player2.name]);

  return (
    <>
      <S.MainPageContainer>
        <S.MainPageTimersContainer>
          <TimerContainer
            defaultTitle={activeGameMode === 'team game' ? 'Team 1' : 'Player 1'}
            player="player1"
            subPlayer="player2"
            defaultColor="red"
            onClickPause={onClickStop('player1')}
            isMainClock
          />

          <S.DesktopPassTurn>
            <S.MainPageButtonContainer>
              {params.get('game_mode') !== 'quick game' && (
                <S.CommonButton
                  variant="primary"
                  $direction="column"
                  $size="large"
                  onClick={onClickPause(setPauseModalActive)}
                  disabled={!inGame}
                  $isPause
                >
                  <Icon variant="pause" />
                  Pause timer
                </S.CommonButton>
              )}
              {params.get('game_mode') !== 'quick game' && inGame ? (
                <S.CommonButton
                  variant="primary"
                  $direction="column"
                  $size="large"
                  onClick={passTurn}
                  disabled={!team.player1.remainingTime || !team.player2.remainingTime}
                  $isSquare
                >
                  <Icon variant="double-arrow" />
                  Pass
                </S.CommonButton>
              ) : !inGame ? (
                <S.CommonButton
                  variant="primary"
                  $direction="column"
                  $size="large"
                  onClick={modalToggleHandler}
                  $isSquare
                >
                  <Icon variant="randomize" />
                  Coin toss
                </S.CommonButton>
              ) : null}
              <S.CommonButton
                variant="primary"
                $direction="column"
                $size="large"
                // disabled={!(team.player1.isPlaying || team.player2.isPlaying)}
                onClick={onClickPause(setVotersModalActive)}
              >
                <Icon variant="voters" />
                Voters&apos; timer
              </S.CommonButton>
            </S.MainPageButtonContainer>
          </S.DesktopPassTurn>

          <TimerContainer
            defaultTitle={activeGameMode === 'team game' ? 'Team 2' : 'Player 2'}
            player="player2"
            subPlayer="player1"
            defaultColor="blue"
            onClickPause={onClickStop('player2')}
            isMainClock
          />
        </S.MainPageTimersContainer>
        <S.MainPageFooter>
          <S.GameModeContainer>
            <Button
              variant="chooser"
              leftTitle="classic game"
              centerTitle="quick game"
              rightTitle="team game"
              onChange={onChangeGameMode as any}
              defaultValue={activeGameMode}
            />
          </S.GameModeContainer>
          <S.DesktopGameSettings>
            <Button
              variant="primary"
              onClick={setGameSettings}
            >
              <Icon variant="settings-1" />
              game settings
            </Button>
          </S.DesktopGameSettings>

          <S.MobileGameSettings>
            <Button
              variant="primary"
              onClick={setGameSettings}
            >
              <Icon variant="settings-1" />
            </Button>
          </S.MobileGameSettings>

          {/* <S.MobileVoters>
            <Button
              variant="primary"
              // disabled={!(team.player1.isPlaying || team.player2.isPlaying)}
              onClick={onClickPause(setVotersModalActive)}
            >
              <Icon variant="voters" />
            </Button>
          </S.MobileVoters> */}

          <S.MainPageButtonsContainer>
            <Button
              variant="primary"
              onClick={toggleFullScreen}
            >
              <Icon variant="fullscreen" />
            </Button>
          </S.MainPageButtonsContainer>
        </S.MainPageFooter>
        <S.MobilePassTurn>
          <Button
            variant="chooser"
            leftTitle="classic game"
            centerTitle="quick game"
            rightTitle="team game"
            onChange={onChangeGameMode as any}
            defaultValue={activeGameMode}
          />

          <S.MainPageButtonContainer>
            {params.get('game_mode') !== 'quick game' && inGame ? (
              <S.CommonButton
                variant="primary"
                $direction="column"
                $size="large"
                onClick={passTurn}
                disabled={!team.player1.remainingTime || !team.player2.remainingTime}
                $isSquare
              >
                <Icon variant="double-arrow" />
                Pass
              </S.CommonButton>
            ) : !inGame ? (
              <S.CommonButton
                variant="primary"
                $direction="column"
                $size="large"
                onClick={modalToggleHandler}
                $isSquare
              >
                <Icon variant="randomize" />
                coin toss
              </S.CommonButton>
            ) : null}
            <S.MainPagePauseButtonContainer>
              <S.CommonButton
                variant="primary"
                $direction="column"
                $size="large"
                // disabled={!(team.player1.isPlaying || team.player2.isPlaying)}
                onClick={onClickPause(setVotersModalActive)}
              >
                <Icon variant="voters" />
                Voters&apos; timer
              </S.CommonButton>
              {params.get('game_mode') !== 'quick game' && (
                <S.CommonButton
                  variant="primary"
                  $direction="column"
                  $size="large"
                  onClick={onClickPause(setPauseModalActive)}
                  disabled={!inGame}
                  $isPause
                >
                  <Icon variant="pause" />
                  Pause timer
                </S.CommonButton>
              )}
            </S.MainPagePauseButtonContainer>
          </S.MainPageButtonContainer>
        </S.MobilePassTurn>
      </S.MainPageContainer>
      <Modal
        onClose={modalToggleHandler}
        isActive={isActiveModal}
        title="coin toss"
      >
        <StartGameForm toggleModal={deactivateModal} />
      </Modal>
      <PauseContainer
        isActivePauseModal={isActivePauseModal}
        setPauseModal={setPauseModalActive}
        title="pause"
        btnTitle="continue"
        isCloseAfterEnd
        pauseSettings={
          params.get('game_mode') !== 'team game' ? classicSettings.pause : teamSettings.pause
        }
      />
      <PauseContainer
        isActivePauseModal={isActiveVotersModal}
        setPauseModal={setVotersModalActive}
        title="Voters’ time"
        btnTitle="restart"
        pauseSettings={
          params.get('game_mode') === 'classic game'
            ? classicSettings.votersTimer
            : params.get('game_mode') === 'team game'
              ? teamSettings.votersTimer
              : quickSettings.votersTimer
        }
      />
      <Flyout
        title={activeFlyout?.title ?? ''}
        isActive={isActiveFlyout}
        onClose={flyoutToggleHandler}
      >
        {activeFlyout?.component}
      </Flyout>
    </>
  );
}

export default MainPage;
